import { PageProps } from '../../components/types'
import HeaderDetail from '../../components/HeaderDetail'
import { PicklistItemCheckMvColumn } from '../../constants/Column'
import Modal from '../../components/Modal'
import Comment from '../../components/Comment'
import Badge from '../../components/Badge'

import OrderPicklistReducer, { 
  INITIAL_STATE, 
  PicklistAction
} from '../../states/PicklistReducer'
import { 
  pdCheckPicklist, 
  checkItemMovement,
  uncheckItemMovement,
  postComment, 
  getRemotePicklistById,
  getMovementsById,
  updateStatusComment
} from '../../states/PicklistAction'

import DataGrid, { RowsChangeData } from 'react-data-grid'

import { 
  useReducer, useEffect, useCallback, useState, useMemo
} from 'react'
import { AiOutlineCheckCircle } from 'react-icons/ai'

import { groupBy as rowGrouper } from 'lodash';

import { DgtPdOrderPicklistMovement } from "../../models"
import { PicklistStatus } from '../../constants/EnumType'

const PicklistCheckDetail = ({ title, id, username, readOnly } : PageProps) => {

  const [ state, dispatch ] = useReducer(
    OrderPicklistReducer, INITIAL_STATE);
  
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  )

  useEffect(() => {
    const fetchPicklists = async (id: string) => {
      const { header, items, comments } = await getRemotePicklistById(id!)
      dispatch({ type: PicklistAction.Inited, payload: { header, 
        items: items.filter(pl => pl.materialType !== 'BULK'),
        comments
      } })
    }

    const fetchMovements = async (orderId: string) => {
      const mvs = await getMovementsById(orderId)
      dispatch({ 
        type: PicklistAction.PicklistItemMovementsUpdated, 
        payload: { 
          itemMovements: mvs.filter(itm => itm.movementType !== '262')
            .filter(itm => itm.materialType !== 'BULK')
            .filter(itm => !itm.extraRefId)
            .filter(itm => itm.materialDescription !== 'DIW')
        }})
    }

    fetchPicklists(id!)
    fetchMovements(id!)
  }, [ id ])

  const onApproved = async () => {
    const updated = await pdCheckPicklist(state.header!, username!)
    dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: updated } })
  }

  const openDialog = () => {
    dispatch({ type: PicklistAction.OpenDialog })
  }

  const closeDialog = () => {
    dispatch({ type: PicklistAction.CloseDialog })
  }

  const onPostComment = useCallback(async (comment: string) => {
    const commentResponse = await postComment(
      state.header?.pk!,
      comment, 
      username!
    )
    dispatch({ type: PicklistAction.PostComment, payload : { comments: [commentResponse] } })
  }, [ state.header?.pk, username ])

  const onActiveComment = useCallback(async (orderId: string, skCommentId: string, active: boolean, version: number) => {
		const updatedComment = await updateStatusComment(orderId, skCommentId, active, version)
    dispatch({ type: PicklistAction.UpdateComment, payload: { comments: [updatedComment] } })
  }, [])

  const { header, showConfirmDialog, comments, itemMovements, items } = state

  useEffect(() => {
    if (itemMovements.length > 0) {
      setExpandedGroupIds(new Set(itemMovements.map(itm => itm.materialCode)))
    }
  }, [ itemMovements ])

  const onRowsChange = async (rows: DgtPdOrderPicklistMovement[], 
    changedInfo: RowsChangeData<DgtPdOrderPicklistMovement>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return
    
    const changedRow = rows[changedRowIdx]

    const updated = changedRow.checkedDateTime! > 0 ?
      await checkItemMovement(changedRow, username!) : await uncheckItemMovement(changedRow)
    dispatch({ 
      type: PicklistAction.PicklistItemMovementsUpdated,
      payload: {
        itemMovements: rows.map(row => row.sk === updated.sk ? updated : row)
      }
    })
  }

  const withDrawItems = new Set(itemMovements.map(itm => itm.materialCode))

  const disabledComplete = itemMovements.length !== itemMovements.filter(mv => mv.checkedDateTime).length
    || header?.status === PicklistStatus.WH_SHORTAGE

  let columns = Array.from(PicklistItemCheckMvColumn)
  if (readOnly) columns.splice(-1)

  const mapBarcodeMovements = useMemo(() => {
    return itemMovements.map(mv => {
      const barcodeItem = items.find(itm => itm.materialCode === mv.materialCode)?.barcodePkg
      if (barcodeItem) return { ...mv, barcode: barcodeItem }
      return mv
    })
  }
  , [ itemMovements, items ])

  return <div className="page-container">
    <HeaderDetail title={`${title} Order : ${id}`} header={header} />
    <div className='flex items-end justify-between mt-1 mb-2'>
      <small>จ่าย {withDrawItems.size} จากทั้งหมด {items.length} รายการ (ไม่รวม Bulk)</small>
      {readOnly ? null : <button className='gap-1 btn-success' onClick={openDialog} disabled={disabledComplete}>
        <AiOutlineCheckCircle />กดเพื่อ COMPLETE
      </button>}
    </div>
    <DataGrid className='mb-3'
      style={{ height: "100%", contain: "none" }}
      columns={columns}
      rows={mapBarcodeMovements}
      rowKeyGetter={(row) => row.id}
      groupBy={['materialCode']}
      rowGrouper={rowGrouper}
      expandedGroupIds={expandedGroupIds}
      onExpandedGroupIdsChange={setExpandedGroupIds}
      defaultColumnOptions={{ resizable: true }}
      onRowsChange={onRowsChange}
      rowHeight={(arg) => {
        if (arg.type === 'GROUP' && arg.row.childRows[0].barcode) return 55
        return 35
      }}
    />
    <small>รายการที่ไม่มีจ่าย</small>
    <div className='flex flex-row flex-wrap gap-3 mt-2 mb-3'>
      {items
        .filter(itm => !Array.from(withDrawItems).includes(itm.materialCode))
        .map(itm => 
          <Badge key={itm.id} variant='outline-muted'>
            {`${itm.materialCode}${itm.barcodePkg ? ` (EAN - ${itm.barcodePkg})` : ''} - ${itm.materialDescription} = ${itm.requestQty} ${itm.materialUnit}`}
          </Badge>
        )
      }
    </div>
    <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={onPostComment} onActive={onActiveComment} username={username}/>
    <Modal show={showConfirmDialog} cancelText='ยกเลิก'
      onHide={closeDialog}
      okText="COMPLETE"
      okTheme='success'
      okAction={onApproved}
      title={`คุณกำลังยืนยันว่านับ Component ของใบเบิกของ ORDER ${id} ครบถ้วน`}>
      <h6>คุณยืนยันใช่หรือไม่ ?</h6>
    </Modal>
  </div>
}

export default PicklistCheckDetail